import {
    FetchResult,
    MutationFunctionOptions,
    MutationHookOptions,
    MutationResult,
    OperationVariables,
    useMutation as useApolloMutation
} from '@apollo/client';
import { DocumentNode } from 'graphql';
import { toast } from 'react-toastify';

interface ToastConfig {
    text?: string;
    type?: 'info' | 'success' | 'warning' | 'error' | 'default' | 'dark' | undefined;
    disabled?: boolean;
}

interface MutationOptions<TData, TVariables> extends MutationFunctionOptions<TData, TVariables> {
    toastConfig?: ToastConfig;
}

type MutationTuple<TData, TVariables> =
    [(options?: MutationOptions<TData, TVariables>) => Promise<FetchResult<TData>>, MutationResult<TData>];


const useMutation = <TData = any, TVariables = OperationVariables>(
    mutation: DocumentNode,
    options?: MutationHookOptions<TData, TVariables>,
): MutationTuple<TData, TVariables> => {
    const [mutationFn, mutationState] = useApolloMutation(mutation, options);

    const mutationWithToast = async (mutationOptions?: MutationOptions<TData, TVariables>) => {
        const toastConfig = mutationOptions?.toastConfig || { disabled: false };

        const data = await mutationFn(mutationOptions);

        if (!toastConfig.disabled) {
            toast(toastConfig?.text || 'Success', { type: toastConfig?.type || 'success' });
        }

        return data;
    };

    return [mutationWithToast, mutationState];
};

export default useMutation;
